import { FC, ReactNode, useEffect, useState } from "react";
import { Box, Divider, Typography } from "@mui/material";
import {
  ArrowDownward,
  Block,
  Cancel,
  CheckCircle,
  Help,
  HideImage,
  RestartAlt,
  Videocam,
} from "@mui/icons-material";
import { Score, TeamReference } from "@/models/pdfData";
import { gameState, useTeamSides } from "@/services/gameState";
import { useAppDataContext } from "@context/AppContext";
import {
  Challenge,
  LastChallenge,
  SamsChallengeType,
} from "@/models/gameState";
import { useGameDataContext } from "@context/GameDataContext";
import { otherTeam } from "@/pdf/utils";
import { getChallengeMomentFromChallenge } from "@/utils/challenge-utils";

const RefereeTabletChallengeInfo: FC<{ side: "left" | "right" }> = ({
  side,
}) => {
  const isChallengeOngoing = gameState("isChallengeOngoing");
  const lastChallenge = gameState("lastChallenge");
  const lastAction = gameState("lastAction");
  const scoreConfiguration = gameState("scoreConfiguration");
  const { addChallengeCounter } = useGameDataContext();
  const currentSet = gameState("currentSet");
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  const teamReference = side === "left" ? leftTeam : rightTeam;

  const [currentTimestamp, setCurrentTimestamp] = useState<number>(Date.now());

  useEffect(() => {
    const timer = setInterval(
      () => {
        setCurrentTimestamp(Date.now());
      },
      isChallengeOngoing ? 100 : 50 * 1000,
    );
    return () => {
      clearInterval(timer);
    };
  }, [isChallengeOngoing]);

  if (
    !lastChallenge ||
    lastChallenge.challenger === otherTeam(teamReference) ||
    (!isChallengeOngoing && !lastAction?.type?.startsWith("[Challenges]"))
  ) {
    return (
      <Box
        onClick={() => {
          if (!scoreConfiguration.videoChallengeAvailable) {
            addChallengeCounter(teamReference, currentSet?.setNumber || 1);
          }
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        py: "1vw",
        mx: "2vw",
        gap: "1vw",
        borderRadius: "1vw",
        border: "0.2vw solid #0000001f",
        minWidth: 0,
        boxShadow: "0px 0px 6px 1px #0000001a",
      }}
    >
      <Videocam
        sx={{
          "@keyframes pulse": {
            "0%": {
              opacity: 1,
            },
            "50%": {
              opacity: 0.3,
            },
            "100%": {
              opacity: 1,
            },
          },
          animation: isChallengeOngoing
            ? "pulse 2s ease-in-out infinite"
            : "unset",
        }}
      />
      <Typography {...typographyProps}>
        {ChallengeTypeTranslations[lastChallenge.challengeType] ||
          lastChallenge.challengeType}
      </Typography>
      <Box
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        width={"100%"}
        flexShrink={0}
        flexGrow={1}
      >
        <Box
          width={"50%"}
          display={"grid"}
          gridTemplateRows={"2fr auto 2fr"}
          justifyItems={"center"}
          alignItems={"self-end"}
          gap={"1vw"}
        >
          {renderChallengeMomentText(lastChallenge)}
          <Divider orientation={"horizontal"} flexItem sx={{ width: "100%" }} />
          {renderChallengeResultIcon(lastChallenge, teamReference)}
        </Box>
        <Divider orientation={"vertical"} flexItem />
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"space-evenly"}
          alignItems={"center"}
          width={"50%"}
        >
          <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
            {renderScore({
              score: lastChallenge.setScoreBeforeChallenge,
              lastChallenge,
              leftTeam,
              rightTeam,
            })}
            <ArrowDownward fontSize={"small"} />
            {renderScore({
              score:
                "setScoreAfterChallenge" in lastChallenge
                  ? lastChallenge.setScoreAfterChallenge
                  : undefined,
              lastChallenge,
              leftTeam,
              rightTeam,
            })}
          </Box>
          <Typography {...typographyProps} fontSize={"1.8vw"}>
            (Score)
          </Typography>
        </Box>
      </Box>
      <Typography {...typographyProps}>
        Dauer:{" "}
        {Math.floor(
          (("durationMs" in lastChallenge && lastChallenge.durationMs) ||
            currentTimestamp -
              lastChallenge.timestamp +
              lastChallenge.timeDelta) / 1000,
        )}
        s
      </Typography>
    </Box>
  );
};

const typographyProps = {
  fontSize: "2.5vw",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

export const ChallengeTypeTranslations: { [key in SamsChallengeType]: string } =
  {
    ANTENNA_CONTACT: "Antenna Touch",
    BALL_IN_OUT: "Ball In/Out",
    BLOCK_TOUCH: "Block Touch",
    FOOT_FAULT_ATTACK_LINE: "Foot Fault (Attack)",
    FOOT_FAULT_BASE_LINE: "Foot Fault (Base)",
    FOOT_FAULT_HALFWAY_LINE: "Foot Fault (Center)",
    GROUND_CONTACT: "Floor touch",
    NET_FAULT: "Net Fault",
    LAST_TOUCH: "Last Touch",
    REACHING_BEYOND_THE_NET: "Reaching Beyond Net",
    TOUCHES_IN_DEFENCE_AND_SERVE_RECEIVE: "Defence Touch",
  };

const ScoreTypography: FC<{ bold: boolean; text: string | number }> = ({
  bold,
  text,
}) => (
  <Typography {...typographyProps} sx={{ fontWeight: bold ? "600" : "400" }}>
    {`${text}`.padStart(2, "0")}
  </Typography>
);
const renderScore = ({
  lastChallenge,
  score,
  leftTeam,
  rightTeam,
}: {
  lastChallenge: Challenge;
  score?: Score;
  leftTeam: TeamReference;
  rightTeam: TeamReference;
}) => {
  return (
    <Box display={"flex"} alignItems={"center"} columnGap={"1vw"}>
      <ScoreTypography
        bold={lastChallenge.teamThatLastScored === leftTeam}
        text={score ? score[leftTeam] : "--"}
      />
      <Typography {...typographyProps}>{":"}</Typography>
      <ScoreTypography
        bold={lastChallenge.teamThatLastScored === rightTeam}
        text={score ? score[rightTeam] : "--"}
      />
    </Box>
  );
};

const renderChallengeResultIcon = (
  challenge: LastChallenge,
  teamReference: TeamReference,
): ReactNode => {
  let declined = false;
  let unresolved = false;
  let successful = false;
  let durationMs: number | undefined;
  let outcome: string | undefined;

  const challengeComplete =
    "outcome" in challenge ||
    "declined" in challenge ||
    "unresolved" in challenge;

  if (challengeComplete) {
    declined = challenge.declined;
    unresolved = challenge.unresolved;
    successful = challenge.successful;
    durationMs = challenge.durationMs;
  }

  if ("outcome" in challenge) {
    outcome = challenge.outcome;
  }

  const challenger = challenge.challenger;

  const refereeChallengeWon =
    challenger === "referee" &&
    outcome === `POINT_${teamReference.toUpperCase()}`;
  const refereeChallengeLost =
    challenger === "referee" &&
    outcome === `POINT_${otherTeam(teamReference).toUpperCase()}`;

  const teamChallengeWon =
    challengeComplete && challenger === teamReference && successful;
  const teamChallengeLost =
    challenger === teamReference &&
    !declined &&
    !unresolved &&
    !successful &&
    durationMs &&
    durationMs > 0;

  let icon = <Help sx={{ opacity: "10%" }} />;

  if (outcome === "DOUBLE_FAULT") {
    icon = <RestartAlt />;
  } else if (declined) {
    icon = <Block />;
  } else if (unresolved) {
    icon = <HideImage />;
  } else if (teamChallengeWon || refereeChallengeWon) {
    icon = <CheckCircle color={"success"} />;
  } else if (teamChallengeLost || refereeChallengeLost) {
    icon = <Cancel color={"error"} />;
  } else {
    icon = <Help sx={{ opacity: "10%" }} />;
  }

  return (
    <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
      {icon}
      <Typography {...typographyProps} fontSize={"1.8vw"}>
        (Ergebnis)
      </Typography>
    </Box>
  );
};

const renderChallengeMomentText = (challenge: LastChallenge): ReactNode => {
  const { challenger } = challenge;

  const challengeMoment = getChallengeMomentFromChallenge(challenge);

  let type = "";

  if (challenger === "referee") {
    type = "SR";
  } else if (challengeMoment === "DURING_RALLY") {
    type = "Mid-Rally";
  } else if (challengeMoment === "AFTER_RALLY") {
    type = "Post-Rally";
  } else if (challengeMoment === "AFTER_CHALLENGE") {
    type = "Counter";
  }

  if (type) {
    return (
      <Typography whiteSpace={"wrap"} fontSize={"2vw"} align={"center"}>
        {type}
        <br />
        {"Challenge"}
      </Typography>
    );
  } else {
    return (
      <Box display={"flex"} flexDirection={"column"} alignItems={"center"}>
        <Help sx={{ opacity: "10%" }} />
        <Typography {...typographyProps} fontSize={"1.8vw"}>
          {"(Zeitpunkt)"}
        </Typography>
      </Box>
    );
  }
};

export default RefereeTabletChallengeInfo;
